import { useMemo, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { RequirementsAppliedSnackbar } from "./RequirementsAppliedSnackbar";
import { useSubmitQuoteCommandLink } from "./useSubmitQuoteCommandLink";
import { useUnauthenticatedQuoteFormState } from "./useUnauthenticatedQuoteFormState";
import {
  buildMultiShipmentProjectQuoteParams,
  toQuoteFormShipment,
  validateMultiShipmentProjectQuote,
} from "./utils";
import { ClientAppEvent } from "../../../../constants";
import { MultiShipmentSubmitQuoteForm } from "../../../Carrier/components/MultiShipmentSubmitQuoteForm/MultiShipmentSubmitQuoteForm";

import type { SubmitQuotePreviewShipment } from "./types";
import type { SubmitMultiShipmentProjectQuoteFormState } from "../../../Carrier/components/MultiShipmentSubmitQuoteForm/types";

const INITIAL_STATE: SubmitMultiShipmentProjectQuoteFormState = {
  equipmentType: "",
  pickupStart: "",
  pickupStartTime: "",
  deliveryEta: "",
  deliveryEtaTime: "",
  currency: "CAD",
  price: null,
  notes: "",
  trackingLinkOffered: false,
  minimumLoads: null,
  maxLoads: null,
};

interface UnauthenticatedMultiShipmentSubmitQuoteFormContainerProps {
  shipment: SubmitQuotePreviewShipment;
  token: string;
  onQuoteSubmitted: VoidFunction;
  onRecoverableFailure: (outcomeCode: string | null) => void;
  // A stop the carrier cannot correct here, discovered on submit.
  onBlockedFailure: (blockedReasonCode: string) => void;
  // A recoverable rejection the carrier can correct without leaving the page.
  submissionError: string | null;
  // Rendered beside Submit: the landing's "No truck available" control.
  secondaryAction?: React.ReactNode;
}

/**
 * The MULTI_SHIPMENT_PROJECT counterpart of
 * UnauthenticatedSubmitQuoteFormContainer: same presentational form the app uses,
 * fed from the command-link preview and submitted through the signed token
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md D2).
 */
export function UnauthenticatedMultiShipmentSubmitQuoteFormContainer(
  props: UnauthenticatedMultiShipmentSubmitQuoteFormContainerProps,
) {
  const {
    shipment,
    token,
    onQuoteSubmitted,
    onRecoverableFailure,
    onBlockedFailure,
    submissionError,
    secondaryAction,
  } = props;
  const posthog = usePostHog();
  const [formValidationError, setFormValidationError] = useState<UIError>(null);
  const {
    formState,
    onValueChanged,
    onPopulateShipmentRequirements,
    snackbarOpened,
    onSnackbarClose,
    carrierTeammatesOpened,
    closeCarrierTeammates,
    openCarrierTeammates,
  } = useUnauthenticatedQuoteFormState({
    shipment,
    initialState: INITIAL_STATE,
  });

  const params = useMemo(
    () => buildMultiShipmentProjectQuoteParams(formState, shipment),
    [formState, shipment],
  );

  const { submitQuote, loading, error } = useSubmitQuoteCommandLink({
    token,
    shipmentId: shipment.info.id,
    quoteType: "multishipment_project",
    params,
    onSubmitted: onQuoteSubmitted,
    onRecoverableFailure,
    onBlockedFailure,
  });

  const onSubmit = () => {
    const formError = validateMultiShipmentProjectQuote(
      formState,
      shipment.project?.loads_available,
    );
    setFormValidationError(formError);
    if (formError != null) {
      return;
    }

    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_SUBMIT_CLICKED, {
      shipment_id: shipment.info.id,
      shipment_type: shipment.info.type,
    });
    submitQuote();
  };

  return (
    <>
      <MultiShipmentSubmitQuoteForm
        secondaryAction={secondaryAction}
        onValueChanged={onValueChanged}
        onSubmit={onSubmit}
        error={error ?? submissionError}
        loading={loading}
        formState={formState}
        quoteRefetchLoading={false}
        canSubmitQuote
        submitBlockReason={null}
        formValidationError={formValidationError}
        onPopulateShipmentRequirements={onPopulateShipmentRequirements}
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        openCarrierTeammates={openCarrierTeammates}
        shipment={toQuoteFormShipment(shipment)}
      />
      <RequirementsAppliedSnackbar
        open={snackbarOpened}
        onClose={onSnackbarClose}
      />
    </>
  );
}
